import { ComponentChildren } from 'preact';
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';

export function BottomSheet(props: {
  open: boolean;
  onClose: () => void;
  children: ComponentChildren;
  ariaLabel?: string;
  dismissOnBackdrop?: boolean;
  centered?: boolean;
  className?: string;
  sheetClassName?: string;
}) {
  if (!props.open) return null;
  const dismissOnBackdrop = props.dismissOnBackdrop !== false;
  const [closing, setClosing] = useState(false);
  const closeTimerRef = useRef<number | null>(null);
  const reducedMotion = useMemo(() => {
    try {
      return !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    } catch {
      return false;
    }
  }, []);

  const close = useCallback(() => {
    if (closing) return;
    if (reducedMotion) {
      props.onClose();
      return;
    }
    setClosing(true);
    // Safety fallback: if animationend doesn't fire for any reason.
    closeTimerRef.current = window.setTimeout(() => {
      props.onClose();
    }, 450);
  }, [closing, props, reducedMotion]);

  // Reset closing flag when sheet re-opens.
  useEffect(() => {
    if (!props.open) return;
    setClosing(false);
  }, [props.open]);

  // NOTE: No scroll lock on body/html - it causes scroll reset in Telegram WebView.
  // Backdrop blocks touch via onTouchMove preventDefault, which is enough for mobile.

  // Allow Telegram BackButton handler (in AppShell) to request closing the top sheet.
  useEffect(() => {
    const onReq = () => close();
    window.addEventListener('aicams-sheet-request-close', onReq);
    return () => window.removeEventListener('aicams-sheet-request-close', onReq);
  }, [close]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    };
  }, []);

  return (
    <div
      class={`outsideModal sheetModal ${closing ? 'sheetClosing' : ''} ${props.centered ? 'outsideModalCentered' : ''} ${props.className || ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={props.ariaLabel || 'Sheet'}
      onAnimationEnd={(e) => {
        // Close only after the OUTER fade-out finishes.
        if (!closing) return;
        if (e.currentTarget !== e.target) return;
        props.onClose();
      }}
      onClick={() => {
        if (dismissOnBackdrop) close();
      }}
      // Block touch-scroll on the backdrop (otherwise iOS will scroll the page behind).
      onTouchMove={(e) => {
        // Only block if the move is on the backdrop, not inside the sheet content.
        // (Sheet content has its own onTouchMove stopPropagation.)
        e.preventDefault();
      }}
    >
      <div
        class={`outsideModalSheet sheetModalSheet ${closing ? 'sheetClosing' : ''} ${props.sheetClassName || ''}`}
        onClick={(e) => e.stopPropagation()}
        onTouchMove={(e) => {
          // Allow scrolling inside the sheet; prevent bubbling to backdrop.
          e.stopPropagation();
        }}
      >
        {props.children}
      </div>
    </div>
  );
}

